import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Button,
    HierarchyList,
    InputWithSearchIcon,
    Label,
    MessageModal,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    RadioButton
} from 'common/components';
import { MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE, MOVE_VALIDATION_ERROR_MESSAGE } from 'common/config';
import { ButtonLabel, ButtonVariant, CategoryField, EntityType, MessageType } from 'common/enums';
import { getHierarchyListColDefs } from 'modules/master-data/config';
import { masterDataActions } from 'modules/master-data/redux';

import './VariantWizardPopup.scss';
import { addValidationError, removeValidationError } from 'common/utils/error';
import {
    ACTION_ITEMS,
    CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR,
    CATEGORY_DESCRIPTION_MAX_LENGTH,
    CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR,
    CATEGORY_NAME_EMPTY_ERROR,
    CATEGORY_NAME_INVALID_CHAR_ERROR,
    CATEGORY_NAME_MAX_LENGTH,
    CATEGORY_NAME_MAX_LENGTH_ERROR,
    CATEGORY_ORDER_EMPTY_ERROR,
    SUB_CATEGORY_NAME_EMPTY_ERROR,
    SUB_CATEGORY_ORDER_EMPTY_ERROR
} from 'common/config';
import { FieldError } from 'common/models';
import CategoryForm from 'common/components/custom/category-form/CategoryForm';
import { SubCategoryField } from 'common/enums/data.enum';
import SubcategoryForm from 'common/components/custom/sub-category-form/SubcategoryForm';
import SearchSelection from 'common/components/custom/search-list/SearchSelection';
import SearchList from 'common/components/custom/search-list/SearchList';
import { EntityVM, MasterDataEntity } from 'common/models/data/entity.model';

type VariantWizardPopupProps = {
    isVisible: boolean;
    closePopupHandler: () => void;
    updateSelectedRadioButton: (entityId: string) => void;
    entityId: string;
    entityType: EntityType;
    modalDetails: any;
    saveHandler: (createNew: string, data: any) => void;
    updateFieldErrors: (errors: Array<FieldError>) => void;
    fieldErrors: Array<FieldError>;
    hierarchicalData: MasterDataEntity[];
    toggleExpansion: (payload: EntityVM) => void;
};

export default function VariantWizardPopup({
    isVisible,
    closePopupHandler,
    updateSelectedRadioButton,
    entityId,
    modalDetails,
    saveHandler,
    updateFieldErrors,
    fieldErrors,
    hierarchicalData,
    toggleExpansion
}: VariantWizardPopupProps) {
    const [popupDetails, setPopupDetails] = useState({
        title: MOVED_SUBCATEGORY_SUCCESSFULLY_TITLE,
        text: MOVE_VALIDATION_ERROR_MESSAGE,
        type: ''
    });
    const [isShowPopup, setShowPopup] = useState<boolean>(false);

    const [createNewSubCategoryFlag, setCreateNewSubCategoryFlag] = useState('');

    const dispatch = useDispatch();
    const [info, setInfo] = useState<any>();
    const [radioButtonForSearch, setRadioButtonForSearch] = useState<string>('');
    const [hideSearch, setHideSearch] = useState<boolean>(false);
    const [selectedRecord, setSelectedRecord] = useState<any>();

    const [wizardScreen, setWizardScreen] = useState(0);

    const handleRadioClicked = (data: any) => {
        setCreateNewSubCategoryFlag('');
        setInfo(data);
        updateSelectedRadioButton(data.id);
    };

    const [entityIdVal, setEntityIdVal] = useState(entityId);

    useEffect(() => {
        setEntityIdVal(entityIdVal);
    }, [entityIdVal]);

    /**
     * Following state variable will be used to deside which radio button should be selected.
     * Existing subcategory radio button group or create new categry or subcategory radio button group.
     */
    //  const [deselectFlag, setDeselectFlag] = useState(false);

    const onClickOfRadioButton = (radioButtonValue: string) => {
        setCreateNewSubCategoryFlag(radioButtonValue);
        dispatch(masterDataActions.deselectRadioButton());
    };

    const [name, setName] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [description, setDescription] = useState('');

    const [subcatName, setSubcatName] = useState('');
    const [subcatSortOrder, setSubcatSortOrder] = useState('');
    const [subcatDescription, setSubcatDescription] = useState('');
    const [searchVal, setSearchVal] = useState<string>('');

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > CATEGORY_DESCRIPTION_MAX_LENGTH) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Description,
                CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }

        setDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > CATEGORY_NAME_MAX_LENGTH) {
            errors = addValidationError(errors, CategoryField.Name, CATEGORY_NAME_MAX_LENGTH_ERROR);
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                CategoryField.Name,
                CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSortOrderChange = (value: string) => {
        const errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        updateFieldErrors(errors);
        setSortOrder(value);
    };

    const handleSubcatDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Description);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > CATEGORY_DESCRIPTION_MAX_LENGTH) {
            errors = addValidationError(
                errors,
                SubCategoryField.Description,
                CATEGORY_DESCRIPTION_MAX_LENGTH_ERROR
            );
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Description,
                CATEGORY_DESCRIPTION_INVALID_CHAR_ERROR
            );
        }

        setSubcatDescription(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSubcatNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, SubCategoryField.Name);
        const reg = new RegExp(/^[a-zA-Z0-9 ]*$/);
        if (event.target.value.length > CATEGORY_NAME_MAX_LENGTH) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                CATEGORY_NAME_MAX_LENGTH_ERROR
            );
        }
        if (!reg.test(event.target.value)) {
            errors = addValidationError(
                errors,
                SubCategoryField.Name,
                CATEGORY_NAME_INVALID_CHAR_ERROR
            );
        }
        setSubcatName(event.target.value);
        updateFieldErrors(errors);
    };

    const handleSubcatSortOrderChange = (value: string) => {
        const errors = removeValidationError(fieldErrors, SubCategoryField.SortOrder);
        updateFieldErrors(errors);
        setSubcatSortOrder(value);
    };

    const onBackButtonClicked = () => {
        if (wizardScreen == 2) {
            updateFieldErrors([]);
            setWizardScreen(1);
        } else if (wizardScreen == 1) {
            updateFieldErrors([]);
            setWizardScreen(0);
        }
    };

    const onSearchChangeHandler = (event: any) => {
        setSearchVal(event.target.value);
    };

    const handleChangeForSearch = (event: React.ChangeEvent<HTMLInputElement>, list: any) => {
        setRadioButtonForSearch(event.target.value);
        setHideSearch(true);
        setSelectedRecord(list);
    };

    const clearSelection = () => {
        setSearchVal('');
        setHideSearch(false);
        setRadioButtonForSearch('');
    };

    return (
        <>
            <Modal
                show={isVisible}
                onClose={() => {
                    setInfo(null);
                    closePopupHandler();
                }}
                style={{ width: '50vw', height: '50vh' }}
                className="hierarchical-view-popup-main-container">
                <ModalHeader>{modalDetails.title}</ModalHeader>
                <ModalBody>
                    {wizardScreen == 0 ? (
                        <div>
                            <Label text={modalDetails.text}></Label>
                            {hideSearch === false && (
                                <InputWithSearchIcon
                                    value={searchVal}
                                    onChange={onSearchChangeHandler}
                                    hideClearButton={false}
                                    placeholder="Search"></InputWithSearchIcon>
                            )}
                            {searchVal.length > 0 ? (
                                hideSearch === false ? (
                                    [
                                        {
                                            id: 1,
                                            name: 'Population growth',
                                            path: ['Country essentials', 'Population'],
                                            entityType: 'Indicator'
                                        },
                                        {
                                            id: 2,
                                            name: 'Population median age',
                                            path: ['Country essentials', 'Population'],
                                            entityType: 'Indicator'
                                        },
                                        {
                                            id: 3,
                                            name: 'Population',
                                            path: ['Country essentials'],
                                            entityType: 'SubCategory',
                                            hasChild: true,
                                            hasIndicator: true
                                        }
                                    ].map((list: any, index: number) => {
                                        return (
                                            <SearchList
                                                radioButtonForSearch={radioButtonForSearch}
                                                handleChangeForSearch={handleChangeForSearch}
                                                key={index}
                                                searchVal={searchVal}
                                                data={list}
                                            />
                                        );
                                    })
                                ) : (
                                    <SearchSelection
                                        clearSelection={clearSelection}
                                        data={selectedRecord}
                                    />
                                )
                            ) : (
                                <HierarchyList
                                    className={
                                        modalDetails.type === 'move' ? 'hierarchy-list-move' : ''
                                    }
                                    isNoDataVisible={false}
                                    data={hierarchicalData}
                                    columnDefs={getHierarchyListColDefs(
                                        handleRadioClicked,
                                        toggleExpansion
                                    )}
                                />
                            )}
                            {modalDetails.type === 'move' && (
                                <div style={{ display: 'flex' }}>
                                    <div className="create-new-category-checkbox-label">
                                        <RadioButton
                                            id={'createNewSubCategory1'}
                                            checked={
                                                createNewSubCategoryFlag === 'createNewCategory'
                                            }
                                            name={'createNewSubCategory'}
                                            value={'createNewCategory'}
                                            onChange={(event: any) => {
                                                onClickOfRadioButton(event.target.value);
                                            }}
                                        />
                                        <Label
                                            style={{ paddingTop: '0.5rem' }}
                                            text="Category/Sub category not found. Create new"></Label>
                                    </div>
                                    <div className="create-new-category-checkbox-label">
                                        <RadioButton
                                            id={'createNewSubCategory2'}
                                            checked={
                                                createNewSubCategoryFlag === 'createNewSubCategory'
                                            }
                                            name={'createNewSubCategory'}
                                            value={'createNewSubCategory'}
                                            onChange={(event: any) => {
                                                onClickOfRadioButton(event.target.value);
                                            }}
                                        />
                                        <Label
                                            style={{ paddingTop: '0.5rem' }}
                                            text="Sub category not found. Create new"></Label>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : wizardScreen == 1 ? (
                        <div className="form-container">
                            <Label
                                style={{
                                    paddingTop: '0.5rem',
                                    fontSize: '1.4rem',
                                    fontWeight: '700',
                                    padding: '1rem 0rem 1rem 0rem'
                                }}
                                text="Create new Category to Map indicator"></Label>
                            <CategoryForm
                                fieldErrors={fieldErrors}
                                name={name}
                                handleNameChange={handleNameChange}
                                sortOrder={sortOrder}
                                handleSortOrderChange={handleSortOrderChange}
                                description={description}
                                handleDescription={handleDescription}
                            />
                        </div>
                    ) : (
                        <div className="form-container">
                            <Label
                                style={{
                                    paddingTop: '0.5rem',
                                    fontSize: '1.4rem',
                                    fontWeight: '700',
                                    padding: '1rem 0rem 1rem 0rem'
                                }}
                                text="Create new Sub-Category to Map indicator"></Label>
                            <SubcategoryForm
                                fieldErrors={fieldErrors}
                                name={subcatName}
                                handleNameChange={handleSubcatNameChange}
                                sortOrder={subcatSortOrder}
                                handleSortOrderChange={handleSubcatSortOrderChange}
                                description={subcatDescription}
                                handleDescription={handleSubcatDescription}
                            />
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <div className="footer-container">
                        <div className="footer-container__left">
                            {wizardScreen == 1 || wizardScreen == 2 ? (
                                <Button
                                    variant={ButtonVariant.Secondary}
                                    onClick={() => onBackButtonClicked()}>
                                    {ACTION_ITEMS.BACK_BUTTON}
                                </Button>
                            ) : (
                                ''
                            )}
                        </div>
                        <div className="footer-container__right">
                            <Button
                                size="medium"
                                type="button"
                                onClick={() => {
                                    if (createNewSubCategoryFlag || info?.id) {
                                        if (createNewSubCategoryFlag == 'createNewCategory') {
                                            if (wizardScreen == 0) {
                                                setWizardScreen(1);
                                            } else if (wizardScreen == 1) {
                                                let errors = fieldErrors;
                                                if (!name.trim()) {
                                                    errors = addValidationError(
                                                        errors,
                                                        CategoryField.Name,
                                                        CATEGORY_NAME_EMPTY_ERROR
                                                    );
                                                }
                                                if (!sortOrder) {
                                                    errors = addValidationError(
                                                        errors,
                                                        CategoryField.SortOrder,
                                                        CATEGORY_ORDER_EMPTY_ERROR
                                                    );
                                                }
                                                if (errors.length === 0) {
                                                    const addedData = {
                                                        name: name,
                                                        order: sortOrder,
                                                        description: description
                                                    };
                                                    setWizardScreen(2);
                                                }
                                                updateFieldErrors(errors);
                                            } else {
                                                let errors = fieldErrors;
                                                if (!subcatName.trim()) {
                                                    errors = addValidationError(
                                                        errors,
                                                        SubCategoryField.Name,
                                                        SUB_CATEGORY_NAME_EMPTY_ERROR
                                                    );
                                                }
                                                if (!subcatSortOrder) {
                                                    errors = addValidationError(
                                                        errors,
                                                        SubCategoryField.SortOrder,
                                                        SUB_CATEGORY_ORDER_EMPTY_ERROR
                                                    );
                                                }
                                                if (errors.length === 0) {
                                                    const addedData = {
                                                        name: subcatName,
                                                        order: subcatSortOrder,
                                                        description: subcatDescription
                                                    };
                                                    saveHandler(createNewSubCategoryFlag, info);
                                                    setName('');
                                                    setSortOrder('');
                                                    setDescription('');
                                                    setSubcatName('');
                                                    setSubcatSortOrder('');
                                                    setSubcatDescription('');
                                                    closePopupHandler();
                                                    setWizardScreen(0);
                                                    setCreateNewSubCategoryFlag('');
                                                }
                                                updateFieldErrors(errors);
                                            }
                                        } else if (
                                            createNewSubCategoryFlag == 'createNewSubCategory'
                                        ) {
                                            saveHandler(createNewSubCategoryFlag, info);
                                        } else {
                                            saveHandler(createNewSubCategoryFlag, info);
                                        }
                                    } else {
                                        setShowPopup(true);
                                    }
                                }}>
                                {(wizardScreen == 0 &&
                                    (createNewSubCategoryFlag == 'createNewSubCategory' ||
                                        createNewSubCategoryFlag == 'createNewCategory')) ||
                                wizardScreen == 1
                                    ? ACTION_ITEMS.NEXT_BUTTON
                                    : wizardScreen == 0
                                    ? ACTION_ITEMS.SUBMIT_BUTTON
                                    : ACTION_ITEMS.SUBMIT_BUTTON}
                            </Button>
                            <Button
                                size="medium"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                    setInfo(null);
                                    closePopupHandler();
                                }}>
                                {ACTION_ITEMS.CANCEL_BUTTON}
                            </Button>
                        </div>
                    </div>
                </ModalFooter>
            </Modal>
            <MessageModal
                headerName={popupDetails.title}
                message={popupDetails.text}
                isVisible={isShowPopup}
                isSecondaryButtonVisible={false}
                type={MessageType.Error}
                primaryButton={ButtonLabel.Ok}
                secondaryButton={ButtonLabel.Cancel}
                onPrimaryAction={() => {
                    setShowPopup(false);
                }}
                onSecondaryAction={() => {
                    setShowPopup(false);
                }}
            />
        </>
    );
}
